import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { applicationsAPI, uploadAPI } from '../services/api';
import SubmissionSuccessDialog from '../components/SubmissionSuccessDialog';

const HowToApplyPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    record_type: ''
  });
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const uploadedAttachments = await Promise.all(
        attachmentFiles.map((file) => uploadAPI.uploadApplicationAttachment(file))
      );
      await applicationsAPI.create({
        ...formData,
        attachment_paths: uploadedAttachments.map((attachment) => attachment.path),
        attachment_names: uploadedAttachments.map((attachment) => attachment.filename),
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
        record_type: ''
      });
      setAttachmentFiles([]);
      setSuccessOpen(true);
    } catch (error) {
      console.error('Error submitting application:', error);
      alert('Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleAttachmentChange = (e) => {
    setAttachmentFiles(Array.from(e.target.files || []));
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-32 pb-16">
      <SubmissionSuccessDialog
        open={successOpen}
        onOpenChange={setSuccessOpen}
        title="Application submitted"
        description="Thank you for applying to Swaarnim Book of Records. Your application has been received successfully."
        nextStep="Our verification team will contact you for supporting documents, photos, or videos if needed."
        buttonText="Got it"
      />
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-red-700 mb-8">
            How to Apply
          </h1>
          
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Application Process</h2>
            
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-red-700 text-white rounded-full flex items-center justify-center font-bold text-xl">
                  1
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Fill the Application Form</h3>
                  <p className="text-gray-700">
                    Complete the form below with your details and describe your achievement or record.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-red-700 text-white rounded-full flex items-center justify-center font-bold text-xl">
                  2
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Submit Details</h3>
                  <p className="text-gray-700">
                    Provide detailed information about your achievement or skill that you want to register.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-red-700 text-white rounded-full flex items-center justify-center font-bold text-xl">
                  3
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Documentation</h3>
                  <p className="text-gray-700">
                    Our team will contact you to request supporting documents, photos, or videos as proof.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-red-700 text-white rounded-full flex items-center justify-center font-bold text-xl">
                  4
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Verification</h3>
                  <p className="text-gray-700">
                    Our team will verify your submission and assess if it meets the criteria for registration.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-red-700 text-white rounded-full flex items-center justify-center font-bold text-xl">
                  5
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Certificate</h3>
                  <p className="text-gray-700">
                    Upon approval, you will receive an official certificate recognizing your achievement.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Application Form */}
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Submit Your Application</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="record_type" className="block text-sm font-medium text-gray-700 mb-2">
                    Record Category
                  </label>
                  <select
                    id="record_type"
                    name="record_type"
                    value={formData.record_type}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  >
                    <option value="">Select Category</option>
                    <option value="Sports">Sports</option>
                    <option value="Arts">Arts & Performance</option>
                    <option value="Academic">Academic</option>
                    <option value="Skills">Unique Skills</option>
                    <option value="Innovation">Innovation</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Describe Your Achievement *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="6"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="Please provide detailed information about your record/achievement, including dates, witnesses, and any supporting evidence you have..."
                  required
                ></textarea>
              </div>

              <div>
                <label htmlFor="attachments" className="block text-sm font-medium text-gray-700 mb-2">
                  Attach Images or PDFs
                </label>
                <input
                  type="file"
                  id="attachments"
                  accept="image/*,.pdf,application/pdf"
                  multiple
                  onChange={handleAttachmentChange}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm file:mr-4 file:rounded-md file:border-0 file:bg-red-700 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-red-800"
                />
                {attachmentFiles.length > 0 && (
                  <div className="mt-3 rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
                    {attachmentFiles.map((file) => (
                      <p key={`${file.name}-${file.size}`} className="truncate">
                        {file.name}
                      </p>
                    ))}
                  </div>
                )}
              </div>

              <Button 
                type="submit"
                disabled={submitting}
                className="w-full bg-red-700 hover:bg-red-800 text-white px-8 py-4 text-lg font-semibold"
              >
                {submitting ? 'Submitting...' : 'Submit Application'}
              </Button>
            </form>
          </div>
          
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded">
            <h3 className="text-lg font-bold text-gray-800 mb-2">Requirements</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>Valid proof of achievement (photos, videos, certificates)</li>
              <li>Witness statements if applicable</li>
              <li>Complete application form</li>
              <li>Registration fee payment (details will be provided after initial review)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowToApplyPage;
